import { rem } from 'polished'
import { useCallback, useRef } from 'react'
import * as React from 'react'
import clsx from 'clsx'
import { defineMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import Clickable from 'components/Common/Clickable'
import Image from 'components/Common/Image'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useScrollEndsObserver from 'hooks/useScrollEndsObserver'
import { scrollByLogicalPosition } from 'lib/dom/logicalScroll'
import Caption from 'LuxKit/Typography/Caption'
import IconButton from 'LuxKit/Button/IconButton'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import useCarouselArrowKind from 'LuxKit/Carousel/useCarouselArrowKind'
import { ALL_PHOTOS_CATEGORY_ID, GALLERY_CONTENT_MAX_WIDTH } from './GalleryModalV2.types'
import type { GalleryCategory } from './GalleryModalV2.types'

const THUMBNAIL_SIZE = 112
const THUMBNAIL_GRID_GAP = 2
const THUMBNAIL_GRID_CELL_SIZE = (THUMBNAIL_SIZE - THUMBNAIL_GRID_GAP) / 2
const THUMBNAIL_MOSAIC_COUNT = 4

const ARROW_SCROLL_PAGE_RATIO = 0.8

export const allPhotosCategoryMessage = defineMessage({
  id: 'WacBM0kPCAHy-Qa7r6Lw',
  defaultMessage: 'All Photos',
})

const StripContainer = styled.div`
  position: relative;
  flex-shrink: 0;
  min-width: 0;
  width: 100%;
  max-width: ${rem(GALLERY_CONTENT_MAX_WIDTH)};
  margin: 0 auto;
`

const StripRoot = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0;
  overflow-x: auto;
  scrollbar-width: none;
  padding-block: ${rem(24)};
  padding-inline: ${rem(20)};

  @media (min-width: ${GALLERY_CONTENT_MAX_WIDTH}px) {
    padding-inline: 0;
  }

  &::-webkit-scrollbar {
    display: none;
  }
`

const ScrollArrow = styled(IconButton)`
  display: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  inset-inline-start: ${rem(12)};
  z-index: 1;
  transition: opacity 0.2s;
  box-shadow: ${props => props.theme.shadow.bottom.medium};

  &.end {
    inset-inline-start: auto;
    inset-inline-end: ${rem(12)};
  }

  &.hidden {
    opacity: 0;
    pointer-events: none;
  }

  ${mediaQueryUp.desktop} {
    display: inline-flex;
  }
`

const CategoryTile = styled(Clickable)<{ $isActive: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(4)};
  flex-shrink: 0;
  padding: ${rem(10)} ${rem(10)} ${rem(6)};
  border-radius: ${props => props.theme.borderRadius.L};
  background: ${props => props.$isActive ? props.theme.palette.neutral.default.six : 'none'};
  border: none;
  cursor: pointer;
`

const CategoryThumbnail = styled.div`
  width: ${rem(THUMBNAIL_SIZE)};
  height: ${rem(THUMBNAIL_SIZE)};
  border-radius: ${props => props.theme.borderRadius.L};
  overflow: hidden;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const ThumbnailMosaic = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: ${rem(THUMBNAIL_GRID_GAP)};
  width: 100%;
  height: 100%;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const CategoryName = styled(Caption)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`

const CategoryLabels = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
  width: ${rem(THUMBNAIL_SIZE)};
`

interface Props {
  categories: Array<GalleryCategory>
  activeCategoryId: string
  onSelectCategory: (categoryId: string) => void
}

function GalleryV2CategoryStrip(props: Props) {
  const { categories, activeCategoryId, onSelectCategory } = props
  const intl = useIntl()

  const stripRef = useRef<HTMLDivElement | null>(null)
  const arrowKind = useCarouselArrowKind()
  const {
    hasReachedHorizontalStart,
    hasReachedHorizontalEnd,
  } = useScrollEndsObserver(stripRef, categories)

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const categoryId = event.currentTarget.dataset.categoryId as string
    onSelectCategory(categoryId)
  }, [onSelectCategory])

  const handleArrowClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const strip = stripRef.current
    if (!strip) return

    const towardsEnd = event.currentTarget.dataset.direction === 'end'
    const distance = strip.clientWidth * ARROW_SCROLL_PAGE_RATIO

    scrollByLogicalPosition(strip, {
      left: towardsEnd ? distance : -distance,
      behavior: 'smooth',
    })
  }, [])

  return <StripContainer>
    <ScrollArrow
      kind={arrowKind}
      variant="dark"
      shape="circle"
      data-direction="start"
      onClick={handleArrowClick}
      className={clsx({ hidden: hasReachedHorizontalStart })}
      aria-label={intl.formatMessage({
        id: 'ZBDlYokr4oprI4DSd-a8',
        defaultMessage: 'Scroll categories left',
      })}
    >
      <LineAngleLeftIcon rtlFlip />
    </ScrollArrow>
    <StripRoot ref={stripRef}>
      {categories.map((category) => {
        const isActive = category.id === activeCategoryId
        const thumbnail = category.images[0]
        const categoryLabel = category.id === ALL_PHOTOS_CATEGORY_ID
          ? intl.formatMessage(allPhotosCategoryMessage)
          : category.label
        const mosaicImages = category.id === ALL_PHOTOS_CATEGORY_ID ? category.images.slice(0, THUMBNAIL_MOSAIC_COUNT) : []
        const showMosaic = mosaicImages.length === THUMBNAIL_MOSAIC_COUNT
        return <CategoryTile
          key={category.id}
          $isActive={isActive}
          onClick={handleClick}
          data-category-id={category.id}
          aria-current={isActive ? 'true' : undefined}
        >
          <CategoryThumbnail>
            {showMosaic && <ThumbnailMosaic>
              {mosaicImages.map((image, imageIndex) => (
                <React.Fragment key={image.id ?? image.url ?? imageIndex}>
                  {image.id && <ResponsiveImage
                    id={image.id}
                    alt={image.title ?? categoryLabel}
                    quality="eco"
                    fit="center"
                    width={THUMBNAIL_GRID_CELL_SIZE}
                    loading="lazy"
                  />}
                  {!image.id && image.url && <Image
                    image={image}
                    alt={image.title ?? categoryLabel}
                    fit="center"
                    loading="lazy"
                  />}
                </React.Fragment>
              ))}
            </ThumbnailMosaic>}
            {!showMosaic && thumbnail?.id && <ResponsiveImage
              id={thumbnail.id}
              alt={thumbnail.title ?? categoryLabel}
              quality="eco"
              fit="center"
              width={THUMBNAIL_SIZE}
              loading="lazy"
            />}
            {!showMosaic && !thumbnail?.id && thumbnail?.url && <Image
              image={thumbnail}
              alt={thumbnail.title ?? categoryLabel}
              fit="center"
              loading="lazy"
            />}
          </CategoryThumbnail>
          <CategoryLabels>
            <CategoryName variant="medium" weight="bold" align="start" title={categoryLabel}>
              {categoryLabel}
            </CategoryName>
            <Caption variant="medium" weight="normal" align="start" colour="neutral-three">
              {intl.formatMessage(
                {
                  id: 'i93ZErvzr3DKHnztyFNx',
                  defaultMessage: '{count, plural, one {# Photo} other {# Photos}}',
                },
                { count: category.images.length },
              )}
            </Caption>
          </CategoryLabels>
        </CategoryTile>
      })}
    </StripRoot>
    <ScrollArrow
      kind={arrowKind}
      variant="dark"
      shape="circle"
      className={clsx('end', { hidden: hasReachedHorizontalEnd })}
      data-direction="end"
      onClick={handleArrowClick}
      aria-label={intl.formatMessage({
        id: 'xiNa4UHZLdaC7qx4MT2H',
        defaultMessage: 'Scroll categories right',
      })}
    >
      <LineAngleRightIcon rtlFlip />
    </ScrollArrow>
  </StripContainer>
}

export default GalleryV2CategoryStrip
